import * as React from 'react';
import * as classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import { action, computed, reaction } from 'mobx';
import * as moment from 'moment';
import { IAppOnlyStore } from '../../../stores/AllStores';
import { PortalSearchItem } from './../PortalSearch';
import { unformat, toFixed } from 'accounting';
import Scroll from '../../../../../common/method/Scroll';
import { SavingIndicator, SavingIndicatorStatus } from './../../../components/indicator';
import { PortalSearchBarField } from '../../../components/layout';
import { CSSTransitionGroup } from 'react-transition-group';
import AnimateHeight from 'react-animate-height';
import { DeleteConfirmation } from '../../../components/popup';
import { PortalCartOptions } from '.';

// Styles
const s = require('./PortalCart.css');
const expandArrows = require('./../../../assets/expand-arrows.svg');
const collapseArrows = require('./../../../assets/collapse-arrows.svg');
const trashCanIcon = require('./../../../assets/trash-can-icon.svg');

// Component
@inject('store')
@observer
export class PortalCart extends React.Component<IAppOnlyStore, {}> {
    private reactToCartHiding = reaction(
        () => this.props.store.cart.showCart,
        (show: boolean) => {
            if (!show && this.cartDiv && typeof this.cartDiv.scrollTo !== 'undefined') {
                this.cartDiv.scrollTo(0, 0);
            }
        }
    );

    public render() {
        const { search, cart, user } = this.props.store;

        return (
            <div
                onClick={this.handleBackgroundShadeClick}
                className={classNames(s.container, {
                    [s.show]: cart.showCart
                })}
            >
                <div className={s.cart} onClick={this.handleCartAreaClick} ref={this.referenceCartDiv}>

                    <div className={classNames(s.loader, {
                        [s.loading]: (!cart.cartFetched && !cart.cartItemFetched) || (cart.cartLoading || cart.cartItemLoading)
                    })}>
                        <SavingIndicator
                            align="center"
                            labels={{
                                saving: 'Loading saved shopping cart...',
                                success: 'Loaded shopping cart' + (cart.hasMultipleCarts ? 's' : '')
                            }}
                            status={
                                cart.cartLoading || cart.cartItemLoading
                                    ? SavingIndicatorStatus.Saving
                                    : cart.cartFetched && cart.cartItemFetched
                                        ? SavingIndicatorStatus.Success
                                        : SavingIndicatorStatus.None
                            }
                        />
                    </div>

                    <div className={s.bar}>
                        <div className={s.total}>
                            <p>
                                {
                                    cart.currentCartTotalCost <= 0
                                        ? 'Cart is empty'
                                        : 'Cart total: $' + toFixed(cart.currentCartTotalCost, 2) +
                                            ' (' +
                                                cart.currentCartTotalUniqueItems + ' item' +
                                                (cart.currentCartTotalUniqueItems > 1 ? 's' : '') +
                                            ')'
                                }
                            </p>
                            {(cart.hasMultipleCarts || cart.cartInfo.hadMoreThanOneCart) && (
                                <button onClick={this.handleTogglingListOfCarts}>
                                    <span>{
                                        cart.hasMultipleCarts
                                            ? cart.showCartsList ? 'Hide saved carts' : 'View saved carts'
                                            : cart.showCartsList ? 'Hide cart details' : 'View cart details'
                                    }</span>
                                    <img src={cart.showCartsList ? collapseArrows : expandArrows} alt="" />
                                </button>
                            )}
                        </div>

                        <CSSTransitionGroup
                            className={s.cartActions}
                            component="div"
                            transitionName="save"
                            transitionAppear={true}
                            transitionEnter={true}
                            transitionLeave={true}
                            transitionAppearTimeout={500}
                            transitionEnterTimeout={500}
                            transitionLeaveTimeout={500}
                        >
                            {(cart.showCart && cart.currentCartTotalCost > 0 && !cart.showSaveCartNameField) && (
                                <button
                                    key="save-for-later-enable"
                                    className={s.saveCartForLater}
                                    onClick={this.handleOpeningSaveCartForLaterField}
                                >
                                    Save cart for later
                                </button>
                            ) || (cart.showCart && cart.currentCartTotalCost > 0 && cart.showSaveCartNameField) && (
                                <div className={s.saveFields} key="save-for-later-fields">
                                    <button className={s.cancel} onClick={this.handleClosingSaveCartForLaterField}>
                                        Cancel
                                    </button>
                                    <form onSubmit={this.handleSavingCart}>
                                        <PortalSearchBarField
                                            className={classNames({
                                                [s.required]: cart.saveCartNameIsRequired
                                            })}
                                            field="cartName"
                                            value={cart.saveCartName}
                                            autoFocus={true}
                                            icon={null}
                                            lightness={0}
                                            label="Name current cart"
                                            onChange={this.handleSaveCartForLaterFieldNameChange}
                                        />
                                        <button className={s.save} onClick={this.handleSavingCart}>
                                            {cart.savingCartForLater ? 'Saving...' : 'Save cart'}
                                        </button>
                                    </form>
                                </div>
                            )}
                            {(
                                (cart.cartFetched && cart.cartItemFetched && !cart.showSaveCartNameField) ||
                                (cart.showCart && !cart.showSaveCartNameField)
                            ) && (
                                <div
                                    className={classNames(s.expand, {
                                        [s.empty]: cart.currentCartTotalCost <= 0
                                    })}
                                    key="open-close-cart"
                                >
                                    {(!cart.showCart && cart.currentCartTotalCost <= 0) && (
                                        <p>Add items to cart to place an order</p>
                                    ) || (cart.showCart) && (
                                        <button className={s.hide} onClick={this.handleTogglingOfCartVisibility}>
                                            Hide cart
                                        </button>
                                    ) || (
                                        <button className={s.show} onClick={this.handleTogglingOfCartVisibility}>
                                            Show cart
                                        </button>
                                    )}
                                </div>
                            )}
                        </CSSTransitionGroup>
                    </div>

                    <AnimateHeight
                        duration={500}
                        height={cart.showCartsList ? 'auto' : 0}
                    >
                        <CSSTransitionGroup
                            component="div"
                            className={s.allCarts}
                            transitionName="single-cart-animation"
                            transitionAppear={false}
                            transitionEnter={false}
                            transitionLeave={true}
                            transitionLeaveTimeout={400}
                        >
                            {(cart.cartInfo.data.map((singleCart) => {
                                const singleCartIsActive: boolean = cart.currentCart !== null
                                    && cart.currentCart.id === singleCart.id;

                                return singleCartIsActive ? null : (
                                    <div key={`cart-${singleCart.id}`} className={s.singleCart}>
                                        <button
                                            className={classNames(s.openCartButton, {
                                                [s.active]: singleCartIsActive
                                            })}
                                            onClick={this.handleSwitchingCart(singleCart.id)}
                                        >
                                            <span>Cart #</span>
                                            <strong>{singleCart.id}</strong>
                                            <span>: </span>
                                            <strong className={s.cartName}>{singleCart.name}</strong>
                                            <span>, </span>
                                            {(singleCart.total) && (
                                                <strong>{
                                                    'contains ' +
                                                    singleCart.totalCount +
                                                    ' items, $' +
                                                    toFixed(singleCart.total, 2)
                                                }</strong>
                                            ) || (
                                                <strong>is empty</strong>
                                            )}
                                            <span>, </span>
                                            <em>last opened </em>
                                            <span>{
                                                singleCartIsActive
                                                    ? 'now'
                                                    : moment(singleCart.updatedAt).fromNow()
                                            }</span>
                                        </button>

                                        {(cart.hasMultipleCarts) && (
                                            <button
                                            className={s.removeCartButton}
                                            onClick={this.handleOpeningCartDeleteDialog(singleCart.id)}
                                            >
                                                <strong>Delete cart</strong>
                                                <img src={trashCanIcon} alt="" />
                                            </button>
                                        )}

                                        {(cart.hasMultipleCarts) && (
                                            <DeleteConfirmation
                                                className={s.deleteCartConfirmation}
                                                show={singleCart.deleteConfirmation}
                                                cancelLabel="Cancel"
                                                confirmationLabel={`Confirm removal of cart #${singleCart.id}`}
                                                onCancel={this.handleClosingCartDeleteDialog(singleCart.id)}
                                                onConfirmDeletion={this.handleDeletingCart(singleCart.id)}
                                            />
                                        )}
                                    </div>
                                );
                            }))}
                        </CSSTransitionGroup>
                    </AnimateHeight>

                    <div className={s.checkout}>

                        <CSSTransitionGroup
                            component="div"
                            className={s.makeModelYearItemsGroup}
                            transitionName="cart-group-animation"
                            transitionAppear={false}
                            transitionEnter={false}
                            transitionLeave={true}
                            transitionLeaveTimeout={400}
                        >
                            {cart.currentCartHasItems && cart.currentCartItemsGroupedByCar.filter((makeModelYear) =>
                                makeModelYear.concat && makeModelYear.items && makeModelYear.items.length > 0
                            ).map((makeModelYear) => (
                                <CSSTransitionGroup
                                    key={makeModelYear.concat}
                                    component="div"
                                    className={s.cartItems}
                                    transitionName="cart-item-animation"
                                    transitionAppear={false}
                                    transitionEnter={false}
                                    transitionLeave={true}
                                    transitionLeaveTimeout={400}
                                >
                                    <h4 key="title" className={s.makeModelYearTitle}>{makeModelYear.concat}</h4>

                                    {makeModelYear.items.filter((i) => !i.hidden).map((item, key) => (
                                        <PortalSearchItem key={key} id={key} item={item} isViewedFromCart={true} />
                                    ))}
                                </CSSTransitionGroup>
                            )) || (
                                    <div className={s.cartEmpty}>
                                        <p>No items in cart - search and add items you would like to order</p>
                                    </div>
                            )}
                        </CSSTransitionGroup>

                        {(cart.currentCartTotalCost > 0) && (
                            <PortalCartOptions />
                        )}

                    </div>

                </div>

                <div className={s.shade} />
            </div>
        );
    }

    private cartDiv: HTMLDivElement = null;
    private referenceCartDiv = (ref) => this.cartDiv = ref;

    @action private handleBackgroundShadeClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.props.store.categories.showCategories = false;
        this.props.store.cart.showCartAction(false, false);

        e.preventDefault();
        e.stopPropagation();
    }

    private handleCartAreaClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.props.store.cart.showShippingMethods = false;
        this.props.store.cart.showShippingAddressesOrWarehouses = false;
        this.props.store.cart.showOtherShippingTypes = false;
        this.props.store.categories.showCategories = false;
        e.stopPropagation();
    }

    @action private handleTogglingListOfCarts = () => {
        const show = !this.props.store.cart.showCartsList;
        this.props.store.cart.showCartsList = show;

        if (show) {
            this.props.store.cart.showCart = true;
        }
    }

    @action private handleTogglingOfCartVisibility = () => {
        const { showCart, showCartAction } = this.props.store.cart;

        if (showCart) {
            showCartAction(false, false);
        } else {
            showCartAction(true, false, true);
        }
    };

    @action private handleClosingSaveCartForLaterField = () => {
        this.props.store.cart.showSaveCartNameField = false;
    }

    @action private handleOpeningSaveCartForLaterField = () => {
        const { cart } = this.props.store;

        cart.saveCartName = cart.currentCart.name !== cart.defaultCartName ? cart.currentCart.name : '';
        cart.showSaveCartNameField = true;
    }

    @action private handleSaveCartForLaterFieldNameChange = (field: string, value: any) => {
        this.props.store.cart.saveCartName = value;
    }

    @action private handleSavingCart = (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
        this.props.store.cart.saveCartForLater();
        e.preventDefault();
        e.stopPropagation();
    }

    @action private handleSwitchingCart = (cartId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.cart.switchToOtherCart(cartId);
    }

    @action private handleOpeningCartDeleteDialog = (cartId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.cart.toggleCartDeleteConfirmation(cartId, true);
    }

    @action private handleClosingCartDeleteDialog = (cartId: number) => () => {
        this.props.store.cart.toggleCartDeleteConfirmation(cartId, false);
    }

    @action private handleDeletingCart = (cartId: number) => () => {
        this.props.store.cart.deleteCart(cartId);
    }
}

export default PortalCart;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/portal/PortalCart/PortalCart.tsx